import type { ReactNode } from "react";
import { Button } from "@/components/ui";
import type { DataTableLabels } from "./tableLabels";
import type { DataTableSelectionBulkAction } from "./tableTypes";

interface DataTableSelectionToolbarProps {
  labels: DataTableLabels;
  selectedIds: ReadonlySet<string>;
  onSelectedIdsChange: (next: Set<string>) => void;
  visibleRowIds: readonly string[];
  bulkAction?: DataTableSelectionBulkAction;
  extra?: ReactNode;
}

export function DataTableSelectionToolbar({
  labels,
  selectedIds,
  onSelectedIdsChange,
  visibleRowIds,
  bulkAction,
  extra,
}: DataTableSelectionToolbarProps) {
  const selectedCount = selectedIds.size;
  const allVisibleSelected =
    visibleRowIds.length > 0 &&
    visibleRowIds.every((id) => selectedIds.has(id));

  const handleSelectVisible = () => {
    const next = new Set(selectedIds);
    for (const id of visibleRowIds) {
      next.add(id);
    }
    onSelectedIdsChange(next);
  };

  const handleClear = () => {
    onSelectedIdsChange(new Set());
  };

  const showBulkAction = bulkAction && !bulkAction.hidden;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {selectedCount > 0 ? (
        <span className="font-medium text-surface-900">
          {labels.selectedCount(selectedCount)}
        </span>
      ) : (
        <span className="text-sm text-surface-600">{labels.selectionHint}</span>
      )}
      {visibleRowIds.length > 0 && !allVisibleSelected ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleSelectVisible}
        >
          {labels.selectAllOnPage}
        </Button>
      ) : null}
      {showBulkAction ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={bulkAction.onClick}
        >
          {bulkAction.label}
        </Button>
      ) : null}
      {extra}
      {selectedCount > 0 ? (
        <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
          {labels.clearSelection}
        </Button>
      ) : null}
    </div>
  );
}
